import * as React from 'react';

import * as Immutable from "immutable"

import Highlighter from "react-highlight-words"

import {Template} from "../../../innertemplatemanager/Template";
import {Paginator} from "../../../react-components/Paginator";
import {TemplateableComponent, TemplateableComponentProps} from "../../../innertemplatemanager/TemplateableComponent";
import {Tag} from "../../../components/tag manager/value objects";

import {ArticleSearchResult} from './ValueObjects'
import {Category, Config} from './../ValueObjects'
import {initElementsWithDataAttributes} from "../../email details to myself";
import {compareStringsAlphabetically} from "../../../lib/utils"

export interface PaginatedResultsProps {
	currentPage:number;
	numPages:number
	onSelectPage:(pageSelected:number) => void;
	isLoading:boolean
	children?:React.ReactNode
}

const Results = (props:PaginatedResultsProps & {config:Config}) => {
	const className = [
		"ResultsPlaceholder",
		props.isLoading ? "loading loading-results" : "",
		props.numPages == 0 ? "no-results" : "",
	].join(" ")

	const results = props.config.resultsDisplay == "infiniteScrolling"
		? <ResultsWithInfiniteScrolling {...props} />
		: <ResultsWithPaginator {...props} />

	return <div className={className}>
		{props.isLoading ? <InfoSearching /> : null}
		{props.numPages == 0 ? <InfoNoResults /> : results}
	</div>
}

const InfoSearching = (props:{}) =>
	<div className="loading-info">
		<div className="text">Searching...</div>
	</div>

const InfoNoResults = (props:{}) =>
	<div className="empty-result-list">
		<div className="text">No results found</div>
	</div>

const ResultsWithInfiniteScrolling = (props:PaginatedResultsProps) => {
	const click = (event:React.FormEvent<{}>) => {
		props.onSelectPage(props.currentPage + 1)
		event.preventDefault()
	}

	const loadMore = props.currentPage + 1 >= props.numPages ? null :
		<div className="paginator">
			<a href="#" className="load-more" onClick={click}>
				<span className="text">Load more results</span>
			</a>
		</div>

	return <div>
		{props.children}
		{loadMore}
	</div>
}

const ResultsWithPaginator = (props:PaginatedResultsProps) =>
	<div>
		{props.children}
		<div className="paginator">
			<Paginator
				currentPage={props.currentPage}
				numPages={props.numPages}
				onSelectPage={props.onSelectPage}/>
		</div>
	</div>

export interface ResultsPlaceholderProps {
	paginatorProps: PaginatedResultsProps
	template:Template
	results:ArticleSearchResult[]
	ajaxPageUrl:string
	canSendDetailsToUser:boolean
	isLoading:boolean
	keywords:string
	config:Config
}

const appendEmailArticleDetailsPlaceholder = (placeholders:StringMap<string>, articleId:number, canSendEmail:boolean) => {
	const cloned = {...placeholders}
	cloned['EmailArticleDetailsToCurrentUser:Attribute'] = canSendEmail ? `data-email-details-to-current-user="${articleId}"` : '';
	return cloned
}

export const ResultsPlaceholder = (props:ResultsPlaceholderProps) => {
	const resultItems = props.results.map((resultItem) => {
		const placeholders = appendEmailArticleDetailsPlaceholder(resultItem.placeholders, resultItem.articleId, props.canSendDetailsToUser)
		return <ResultItem
			key={resultItem.articleId.toString()}
			resultItem={resultItem}
			template={props.template}
			placeholders={placeholders}
			keywords={props.keywords}
			ajaxPageUrl={props.ajaxPageUrl}
		/>
	})

	return <Results {...props.paginatorProps} config={props.config}>{resultItems}</Results>
}

export interface ResultsGroupedByCategoriesPlaceholderProps extends ResultsPlaceholderProps {
	categoryIdsToHighlight:Immutable.Set<number>
	categories:Category[]
}

/**
 * Group all search results by category, and then show a category heading before each group of results.
 */
export class ResultsGroupedByCategoriesPlaceholder extends React.Component<ResultsGroupedByCategoriesPlaceholderProps, {}> {

	private categoryIdsToHighlight():Immutable.Set<number> {
		return this.props.categoryIdsToHighlight.size > 0
			? this.props.categoryIdsToHighlight
			: Immutable.Set(this.props.categories.map((category) => category.getNumericId()))
	}

	private groupByCategory():Map<Category, ArticleSearchResult[]> {
		const byCategory = new Map<Category, ArticleSearchResult[]>()
		const categoryIdsToHighlight = this.categoryIdsToHighlight()

		const append = (category:Category, item:ArticleSearchResult) => {
			let result = byCategory.get(category)
			if (result === undefined) {
				result = []
				byCategory.set(category, result)
			}
			result.push(item)
		}

		this.props.results.forEach((resultItem) => {
			if (resultItem.categories.length == 0) {
				append(Category.UNCATEGORISED, resultItem)
			} else {
				resultItem.categories.filter((category) =>
					categoryIdsToHighlight.has(category.getNumericId())
				).forEach((category) => {
					append(category, resultItem)
				})
			}
		})

		return byCategory
	}

	render() {
		const categoryGroups:JSX.Element[] = []

		Array.from(this.groupByCategory().entries()).sort((c1, c2) => compareStringsAlphabetically(c1[0].getLabel(), c2[0].getLabel())).forEach((entry) => {
			const category = entry[0]
			const articles = entry[1]

			categoryGroups.push(
				<div className="category-results" key={`category-${category.getId()}`}>
					<div className="category-name">{category.getLabel()}</div>
					{articles.map((article) =>
						<ResultItem
							key={article.articleId.toString()}
							resultItem={article}
							keywords={this.props.keywords}
							template={this.props.template}
							placeholders={appendEmailArticleDetailsPlaceholder(article.placeholders, article.articleId, this.props.canSendDetailsToUser)}
							ajaxPageUrl={this.props.ajaxPageUrl}
						/>
					)}
				</div>
			)

		})

		return <Results {...this.props.paginatorProps} config={this.props.config}>{categoryGroups}</Results>
	}
}

interface ResultItemProps extends TemplateableComponentProps {
	key:string
	resultItem:ArticleSearchResult
	ajaxPageUrl:string
	keywords:string
}

class ResultItem extends TemplateableComponent<ResultItemProps, {}> {
	renderPlaceholders():StringMap<React.ReactElement<any>|null> {
		const keywords = this.props.keywords != null ? this.props.keywords.split(/[\s,]+/) : []

		const highlightedTitlePlaceholder = this.props.resultItem.placeholders.hasOwnProperty("Title")
			? <Highlighter highlightClassName="highlight" searchWords={keywords} textToHighlight={this.props.resultItem.placeholders["Title"]} />
			: null

		return {
			"Categories": this.props.resultItem.categories.length == 0 ? null : <CategoriesList categories={this.props.resultItem.categories} />,
			"Tags": this.props.resultItem.tags.length == 0 ? null : <TagList tags={this.props.resultItem.tags} />,
			"Title:Highlighted": highlightedTitlePlaceholder
		};
	}

	protected placeholdersDidRender(node:Element):void {
		initElementsWithDataAttributes(this.props.ajaxPageUrl, node)
	}
}

const CategoriesList = (props:{categories:Category[]}) =>
	<ul className="list category-list">
		{props.categories.map((category) =>
			<li key={`tag-${category.getId()}`} className="list-item category-list-item">{category.getLabel()}</li>
		)}
	</ul>

const TagList = (props:{tags:Tag[]}) =>
	<ul className="list tag-list">
		{props.tags.map((tag) =>
			<li key={`tag-${tag.id}`} className="list-item tag-list-item">{tag.name}</li>
		)}
	</ul>



// WEBPACK FOOTER //
// ./src/news2/advancedSearch/results/ResultsPlaceholder.tsx